import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock, Play, Clock, Users, CheckCircle, DollarSign } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import ErrorDisplay from '@/components/ErrorDisplay';
import StandardsExplainer from '@/components/StandardsExplainer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_COLORS = {
  'Author': 'bg-blue-100 text-blue-700',
  'Reviewer': 'bg-green-100 text-green-700',
  'Compliance Officer': 'bg-purple-100 text-purple-700',
  'Technical SME': 'bg-amber-100 text-amber-700',
  'Approver': 'bg-red-100 text-red-700',
  'Safety Officer': 'bg-orange-100 text-orange-700',
  'Clinical SME': 'bg-pink-100 text-pink-700'
};

export default function PlaybookDetails({ user, logout }) {
  const { playbookId } = useParams();
  const navigate = useNavigate();
  const [playbook, setPlaybook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    fetchPlaybookDetails();
  }, [playbookId]);

  const fetchPlaybookDetails = async () => {
    try {
      const token = localStorage.getItem('standari_token');
      const response = await axios.get(
        `${BACKEND_URL}/api/playbooks/${playbookId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPlaybook(response.data);
      setLoading(false);
    } catch (err) {
      setError(err);
      setLoading(false);
    }
  };

  const startWorkflow = async () => {
    try {
      setStarting(true);
      const token = localStorage.getItem('standari_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/playbooks/${playbookId}/start`,
        { assigned_users: {}, metadata: {} },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      navigate(`/workflows/${response.data.workflow_id}`);
    } catch (err) {
      setError(err);
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading playbook...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!playbook) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-5xl mx-auto">
          <ErrorDisplay error={error} />
        </div>
      </div>
    );
  }

  const isLocked = playbook.locked;
  const totalTime = playbook.steps.reduce((sum, step) => {
    if (step.time_expectation) {
      const match = step.time_expectation.match(/(\d+)/);
      return sum + (match ? parseInt(match[0]) : 0);
    }
    return sum;
  }, 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button 
            onClick={() => navigate('/playbooks')} 
            variant="ghost"
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Playbooks
          </Button>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {isLocked && <Lock className="h-6 w-6 inline mr-2" />}
                {playbook.name}
              </h1>
              <p className="text-gray-600 mb-4">{playbook.description}</p>
              <div className="flex items-center space-x-4">
                <Badge className="bg-gray-100 text-gray-700">
                  {playbook.industry.join(', ')}
                </Badge>
                <Badge className="bg-purple-100 text-purple-700">
                  {playbook.tier_required.toUpperCase()} Tier
                </Badge>
              </div>
            </div>
            {!isLocked && (
              <Button 
                onClick={startWorkflow}
                disabled={starting}
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 ml-4"
              >
                {starting ? 'Starting...' : (
                  <>
                    <Play className="h-5 w-5 mr-2" />
                    Start Workflow
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6">
            <ErrorDisplay error={error} onRetry={fetchPlaybookDetails} />
          </div>
        )}

        {/* Locked Warning */}
        {isLocked && (
          <Card className="mb-6 border-2 border-amber-200 bg-amber-50">
            <CardContent className="pt-6">
              <div className="flex items-start">
                <Lock className="h-6 w-6 text-amber-600 mt-0.5 mr-3" />
                <div className="flex-1">
                  <h3 className="font-semibold text-amber-900 mb-1">
                    Upgrade to {playbook.required_tier} Tier
                  </h3>
                  <p className="text-sm text-amber-800 mb-3">
                    This playbook requires {playbook.required_tier} tier access. 
                    Upgrade to unlock guided workflows that eliminate expert bottlenecks.
                  </p>
                  <Button className="bg-amber-600 hover:bg-amber-700">
                    Upgrade Now
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Clock className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Est. Time</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {totalTime > 0 ? `${totalTime} hours` : 'Varies'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Steps</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {playbook.steps.length} guided steps
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Users className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Roles</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {playbook.roles_required.length} roles
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Triggers */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>When to Use This Playbook</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {playbook.triggers.map((trigger, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  <span className="text-gray-700">{trigger}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Standards */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Standards & Compliance</CardTitle>
          </CardHeader>
          <CardContent>
            <StandardsExplainer selectedStandards={playbook.standards_required} />
          </CardContent>
        </Card>

        {/* Steps */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Workflow Steps</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {playbook.steps.map((step, index) => (
                <div 
                  key={step.id} 
                  className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg"
                >
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold">
                      {index + 1}
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-semibold text-gray-900">{step.name}</h4>
                        <Badge className={`mt-1 ${ROLE_COLORS[step.role_owner] || 'bg-gray-100 text-gray-700'}`}>
                          {step.role_owner}
                        </Badge>
                      </div>
                      {step.time_expectation && (
                        <span className="text-sm text-gray-500">{step.time_expectation}</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{step.description}</p>
                    <div className="text-xs text-gray-500">
                      <strong>Why it matters:</strong> {step.why_it_matters}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Expected Output */}
        <Card>
          <CardHeader>
            <CardTitle>What You'll Get</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700">{playbook.expected_output}</p>
          </CardContent>
        </Card>

        {/* Bottom CTA */}
        {!isLocked && (
          <div className="mt-8 text-center">
            <Button 
              onClick={startWorkflow}
              disabled={starting}
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 px-8"
            >
              {starting ? 'Starting Workflow...' : (
                <>
                  <Play className="h-5 w-5 mr-2" />
                  Start This Workflow
                </>
              )}
            </Button>
            <p className="text-sm text-gray-500 mt-2">
              You'll be guided through each step with clear instructions
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
